/**
 * ManagerHub — manager landing with tab strip; Trend is default.
 *
 * Tabs:
 *   - Trend (default)        : team rolling chart + per-rep mini sparkline + triggers
 *   - Per-rep cards          : full grid of rep cards (existing ManagerView)
 *   - Pipeline               : pipeline tab (delegates to existing ManagerView Pipeline view)
 *   - Coaching               : coaching feed for Manager's reps
 *
 * The "Per-rep cards" tab renders the existing ManagerView component to preserve
 * everything already shipped in the sprint integration.
 */
import { useState } from "react";
import ManagerView from "./ManagerView";
import TrendPage from "@/pages/TrendPage";
import { useTeamTrend } from "@/lib/useTrend";
import TrendSparkline from "@/components/TrendSparkline";
import { api, getSession, supabase, getCurrentTenant } from "@/lib/session";
import { useEffect } from "react";
import { DnaGlyph } from "@/components/DnaGlyph";
import { ManagerFlaggedTab } from "./ManagerFlaggedTab";
import ActivityVerdictTab, { loadVerdict, type VerdictResponse } from "@/components/dashboards/ActivityVerdictTab";
import { DerailmentWatchPanel } from "@/components/manager/DerailmentWatchPanel";

function repSlug(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, "-");
}
function readRepScore(name: string): number | null {
  try {
    const raw = localStorage.getItem(`aip:pipeline-dna:${repSlug(name)}-score`);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

type Tab = "verdict" | "trend" | "cards" | "pipeline" | "flagged" | "coaching" | "derailment";

interface CoachingNote {
  id: string;
  body: string;
  rep_user_id: string;
  rep_name?: string;
  created_at: string;
}

function ManagerTrendTab() {
  const team = useTeamTrend("team");
  const reps: any[] = team.team?.reps ?? [];
  return (
    <div className="space-y-5">
      <TrendPage />
      {reps.length > 0 && (
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="mb-3 font-medium" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
            Per-rep mini trends
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {reps.map((r: any) => {
              const values: number[] = (r.weeks ?? []).map((w: any) => Number(w.outreach ?? 0));
              return (
                <a key={r.user_id} href={`#/dashboards/manager?as=manager&rep=${r.user_id}`} className="rounded-lg border border-neutral-200 bg-white p-3 shadow-sm hover:shadow">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <span
                        role="link"
                        tabIndex={0}
                        aria-label={`Open Pipeline DNA for ${r.name ?? "rep"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          window.location.hash = `/pipeline-dna/${repSlug(r.name ?? "")}`;
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            e.preventDefault();
                            window.location.hash = `/pipeline-dna/${repSlug(r.name ?? "")}`;
                          }
                        }}
                        className="shrink-0 inline-flex items-center justify-center rounded-md p-0.5 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40 cursor-pointer"
                      >
                        <DnaGlyph score={readRepScore(r.name ?? "")} size={20} ariaLabel={`${r.name ?? "Rep"} pipeline DNA`} />
                      </span>
                      <span className="text-sm font-medium text-neutral-900 truncate">{r.name ?? "Rep"}</span>
                    </span>
                    <span className="text-[11px] text-neutral-500">{r.role ?? ""}</span>
                  </div>
                  <TrendSparkline values={values} width={240} height={36} />
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ManagerCoachingTab() {
  const [notes, setNotes] = useState<CoachingNote[]>([]);
  const [body, setBody] = useState("");
  const [repId, setRepId] = useState("");
  const [reps, setReps] = useState<{ id: string; name: string }[]>([]);
  const session = getSession();

  useEffect(() => {
    (async () => {
      try {
        const r = await api<{ notes: CoachingNote[] }>("/api/coaching/notes").catch(() => ({ notes: [] }));
        setNotes(r?.notes ?? []);
      } catch { /* tolerate */ }
      try {
        const u = await api<{ users: { id: string; full_name: string }[] }>("/api/users").catch(() => null);
        if (u?.users) setReps(u.users.map((x) => ({ id: x.id, name: x.full_name })));
      } catch { /* tolerate */ }
    })();
  }, []);

  async function add() {
    if (!body.trim() || !repId) return;
    try {
      await api("/api/coaching/note", {
        method: "POST",
        body: JSON.stringify({ rep_user_id: repId, body, visibility: "rep_visible" }),
      });
      setBody("");
      const r = await api<{ notes: CoachingNote[] }>("/api/coaching/notes").catch(() => ({ notes: [] }));
      setNotes(r?.notes ?? []);
    } catch { /* tolerate */ }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <h2 className="mb-3 text-lg font-semibold" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>Coaching</h2>
      {session?.role === "manager" && (
        <div className="mb-5 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
          <h3 className="mb-2 text-sm font-medium text-neutral-700">Add a note</h3>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select className="rounded border border-neutral-300 px-2 py-1.5 text-sm" value={repId} onChange={(e) => setRepId(e.target.value)} data-testid="coaching-rep-select">
              <option value="">Choose rep...</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <input className="flex-1 rounded border border-neutral-300 px-2 py-1.5 text-sm" placeholder="Note body" value={body} onChange={(e) => setBody(e.target.value)} data-testid="coaching-body" />
            <button className="rounded bg-[#DC2626] px-3 py-1.5 text-sm font-medium text-white" onClick={add} disabled={!body.trim() || !repId}>Save</button>
          </div>
        </div>
      )}
      <div className="rounded-lg border border-neutral-200 bg-white shadow-sm">
        {notes.length === 0 ? (
          <p className="p-4 text-sm text-neutral-500">No notes yet.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {notes.map((n) => (
              <li key={n.id} className="p-3">
                <div className="text-xs text-neutral-500">{n.rep_name ?? n.rep_user_id} - {new Date(n.created_at).toLocaleDateString()}</div>
                <div className="text-sm text-neutral-800">{n.body}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function ManagerHub() {
  // TICKET 2 — verdict is the default tab when any rep is INSUFFICIENT today.
  // The verdict count is pre-fetched on mount and the tab label always shows
  // the INSUFFICIENT count so the manager cannot dismiss the signal by
  // switching tabs.
  const [verdictSummary, setVerdictSummary] = useState<VerdictResponse | null>(null);
  const [verdictLoaded, setVerdictLoaded] = useState(false);
  const [tab, setTab] = useState<Tab>("verdict");

  useEffect(() => {
    let cancelled = false;
    loadVerdict("day").then((r) => {
      if (cancelled) return;
      setVerdictLoaded(true);
      if (r) {
        setVerdictSummary(r);
        // Default to verdict tab whenever someone is INSUFFICIENT today.
        // If everyone is on-plan or proof-ok, leave the manager on verdict
        // anyway — the doctrine is "no hiding allowed," and a green view
        // is part of the proof.
      }
    });
    return () => { cancelled = true; };
  }, []);

  const insufficientCount = verdictSummary?.summary.insufficient ?? 0;

  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7" }}>
      <div className="mx-auto max-w-6xl px-4 pt-6 pb-2">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>Manager view</h1>
        <p className="text-sm text-muted-foreground mt-1">Per-rep team activity, deals in motion, and coaching triggers.</p>
      </div>
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-6xl gap-2 px-4">
          {([
            { id: "verdict", label: "Activity verdict" },
            { id: "trend", label: "Trend" },
            { id: "cards", label: "Per-rep cards" },
            { id: "pipeline", label: "Pipeline" },
            { id: "flagged", label: "Flagged" },
            { id: "coaching", label: "Coaching" },
            { id: "derailment", label: "Derailment Watch" },
          ] as { id: Tab; label: string }[]).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-3 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-[#DC2626] text-[#0B0B0F]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}
              data-testid={`manager-tab-${t.id}`}
            >
              <span>{t.label}</span>
              {t.id === "verdict" && verdictLoaded && insufficientCount > 0 && (
                <span
                  data-testid="manager-tab-verdict-badge"
                  style={{
                    marginLeft: 6,
                    display: "inline-block",
                    minWidth: 18,
                    padding: "1px 6px",
                    borderRadius: 999,
                    background: "#DC2626",
                    color: "white",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: "16px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >{insufficientCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>
      {tab === "verdict" && <ActivityVerdictTab onLoad={setVerdictSummary} />}
      {tab === "trend" && <ManagerTrendTab />}
      {tab === "cards" && <ManagerView />}
      {tab === "pipeline" && <ManagerView />}
      {tab === "flagged" && <ManagerFlaggedTab />}
      {tab === "coaching" && <ManagerCoachingTab />}
      {tab === "derailment" && (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <DerailmentWatchPanel />
        </div>
      )}
    </div>
  );
}
